import { FC, useState, useEffect, useCallback, useRef } from 'react'
import Gleap from 'gleap'
import * as Sentry from '@sentry/react'
import '@reapit/elements/dist/index.css'
import {
  Button,
  FlexContainer,
  TextArea,
  PageHeader,
  Tile,
  Toggle,
  ElToggleItem,
  Input,
  Grid,
  ColQuarter,
  Select,
  Snack,
  PersistentNotification,
  TextBase,
  CardWrap,
  ColHalf,
} from '@reapit/elements'
import Pusher from 'pusher-js'
import useApi from '../hook/useApi'

/* START THEME IMPORTS --------- */
import UpdateSettingsBlock from './updateSettings'
import FinishedBlock from './finished'
import LoginFailedBlock from './loginFailed'
import InProgressBlock from './inProgress'
import SaveToCrm from './saveToCrm'
import ResumeBlock from './resume'
import AutoCallHeader from "./autoCallHeader";
import Notification from "./notification";
import OutageMemo from './outageMemo'
import MaintenanceBlock from './maintenance'
/* END THEME IMPORTS --------- */

/* START DEFAULT VARIABLES / SETTINGS --------- */
const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const crm = urlParams.get('crm') ?? ''
let defaultCrmKey = urlParams.get('crmKey') ?? ''
let defaultCrmUrl = urlParams.get('crmUrl') ?? ''
let defaultDestination = urlParams.get('destination') ?? ''
let defaultEndpoint = urlParams.get('endpoint') ?? ''
let uuid = urlParams.get('uuid') ?? ''
let loginToken = urlParams.get('login_token') ?? ''
let defaultCallTriggerUrl = urlParams.get('call_trigger_url') ?? ''
const defaultLeadCategory = urlParams.get('lead_category') ?? ''
let timeoutDuration = 60000
if (process.env.APP_ENV !== 'production') {
  timeoutDuration = 10000
  //uuid = 'emailaddress'
}
/* END DEFAULT VARIABLES / SETTINGS --------- */

interface Task {
  code: string
  name: string
  description?: string
  date_follow_up?: string
}

export const Wrapper: FC = () => {
  const [callInProgress, setCallInProgress] = useState<boolean>(false)
  const [finished, setFinished] = useState<boolean>(false)
  const [noAnswer, setNoAnswer] = useState<boolean>(false)
  const [aiFailed, setAiFailed] = useState<boolean>(false)
  const [incoming, setIncoming] = useState<boolean>(false)
  const [shortCall, setShortCall] = useState<boolean>(false)
  const [settingsRequired, setSettingsRequired] = useState<boolean>(false)
  const [loginFailed, setLoginFailed] = useState<boolean>(false)
  const [aiFinished, setAiFinished] = useState<boolean>(false)
  const [omitAi, setOmitAi] = useState<boolean>(false)
  const [voicemailSummary, setVoicemailSummary] = useState<boolean>(false)
  const [objections, setObjections] = useState<any[]>([])
  const [callId, setCallId] = useState<string>('')
  const [userName, setUserName] = useState<string>('')
  const [userEmail, setUserEmail] = useState<string>('')
  const [userId, setUserId] = useState<string>('')
  const [destination, setDestination] = useState<string>(defaultDestination ?? '')
  const [objectionId, setObjectionId] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [aiSummary, setAiSummary] = useState<string>('')
  const [aiHighlights, setAiHighlights] = useState<string>('')
  const [summaryUsed, setSummaryUsed] = useState<boolean>(true)
  const [summary] = useState<string>('')
  const [crmKey, setCrmKey] = useState<string>(defaultCrmKey ?? '')
  const [contactKey] = useState<string>(defaultCrmKey ?? '')
  const [endpoint] = useState<string>(defaultEndpoint ?? '')
  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [validation, setValidation] = useState<string[]>([])
  const [validationItems, setValidationItems] = useState<string[]>([])
  const [viewing, setViewing] = useState<boolean>(false)
  const [hideViewing] = useState<boolean>(crm == 'STREET')
  const [appraisal, setAppraisal] = useState<boolean>(false)
  const [hideAppraisal] = useState<boolean>(crm == 'STREET')
  const [instruction, setInstruction] = useState<boolean>(false)
  const [voicemail, setVoicemail] = useState<boolean>(true)
  const [applicants, setApplicants] = useState<boolean>(false)
  const [followUpType, setFollowUpType] = useState<string>('both')
  const [followUpRequired, setFollowUpRequired] = useState<boolean>(true)
  const [followUpDate, setFollowUpDate] = useState<string>('')
  const [actionDate, setActionDate] = useState<string>('')
  const [categoryId, setCategoryId] = useState<string>('')
  const [crmUrl, setCrmUrl] = useState<string>(defaultCrmUrl ?? '')
  const [callTriggerUrl, setCallTriggerUrl] = useState<string>(defaultCallTriggerUrl ?? '')
  const [resume, setResume] = useState<any>(false)
  const [callsAwaitingSummary, setCallsAwaitingSummary] = useState<any[]>([])
  const [messageTemplates, setMessageTemplates] = useState<any[]>([])
  const [messageTemplateBody, setMessageTemplateBody] = useState<string>('')
  const [messageTemplateId, setMessageTemplateId] = useState<string>('')
  const [templatePlaceholderValues, setTemplatePlaceholderValues] = useState<Record<string, string>>({})
  const [templatePlaceholders, setTemplatePlaceholders] = useState<string[]>([])
  const [selectedMessageTemplate, setSelectedMessageTemplate] = useState<string>('')
  const [whatsappFollowup, setWhatsappFollowup] = useState<boolean>(false)
  const [stitchEnabled, setStitchEnabled] = useState<boolean>(false)
  const [stitchDefaults, setStitchDefaults] = useState<string[]>([])
  const [callDirection, setCallDirection] = useState<string>('outbound')
  const [resumeId, setResumeId] = useState<string>('')
  const [cancelId, setCancelId] = useState<string>('')
  const [toggleAutoCallHeader, setToggleAutoCallHeader] = useState<boolean>(true)
  const [disableFollowUp, setDisableFollowUp] = useState<boolean>(false)
  const [leadCategory, setLeadCategory] = useState<string>(defaultLeadCategory ?? '')
  const [displayLoadingText, setDisplayLoadingText] = useState<string>('Loading Details')

  const [outageMessage, setOutageMessage] = useState<string>('')
  const [outageSeverity, setOutageSeverity] = useState<number>(0)

  const disableCalls = false
  const version = '1.1.0'
  const dateSetRef = useRef(false);

  let retryTimeout: any = null

  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null)
  const resumeRef = useRef<NodeJS.Timeout | null>(null)

  const { makeRequest, setBearerToken, bearerToken, setXAgency, createSessionRequest } = useApi()

  const apiPath = process.env.REACT_APP_PP_API_URL
  const pusherKey = process.env.REACT_APP_PP_PUSHER_KEY
  const gleapInitialized = useRef(false);

  const AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED';
  const CALL_IN_PROGRESS = 'CALL_IN_PROGRESS';
  const CALL_FAILED = 'CALL_FAILED';

  const fetchMessageTemplates = async () => {
    try {
      const response = await makeRequest('stitch/message-templates', 'GET')
      const results = response.data
      setMessageTemplates(results)
    } catch (error) {
      console.error('Error loading stitch:', error)
    }
  }

  useEffect(() => {
    authenticateUser()
  }, [])

  useEffect(() => {
    if (Gleap.isUserIdentified()) {
      const customData = {
        callInProgress: callInProgress.toString(),
        finished: finished.toString(),
        destination: destination || '',
        aiFailed: aiFailed.toString(),
        aiFinished: aiFinished.toString(),
        voicemailSummary: voicemailSummary.toString(),
        objections: objections.length.toString(),
        callId: callId.toString(),
        notes: notes || '',
        summary: summary || '',
        crmKey: crmKey || '',
        loading: loading.toString(),
        saving: saving.toString(),
        validation: JSON.stringify(validation),
        validationItems: JSON.stringify(validationItems),
        viewing: viewing.toString(),
        appraisal: appraisal.toString(),
        instruction: instruction.toString(),
        voicemail: voicemail.toString(),
        followUpType: followUpType || '',
        followUpRequired: followUpRequired.toString(),
        followUpDate: followUpDate || '',
        actionDate: actionDate || '',
        categoryId: categoryId.toString(),
        crmUrl: crmUrl || '',
        version: version || '',
      }

      Gleap.attachCustomData(customData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    callInProgress,
    finished,
    destination,
    aiFailed,
    aiFinished,
    voicemailSummary,
    objections,
    callId,
    notes,
    summary,
    crmKey,
    loading,
    saving,
    validation,
    validationItems,
    viewing,
    appraisal,
    instruction,
    voicemail,
    followUpType,
    followUpRequired,
    followUpDate,
    actionDate,
    categoryId,
    crmUrl,
    version,
  ])
  /* END GLEAP INTEGRATION --------- */


  useEffect(() => {
    if (bearerToken) {
      loadObjections()
      if (contactKey) {
        // if we have a contact try and call it
        startVoipCall(contactKey, endpoint, crm, destination, leadCategory)
      }
      if (stitchEnabled && messageTemplates && messageTemplates.length < 1) {
        fetchMessageTemplates()
      }
    }
  }, [bearerToken])

  // if no summary this method is used to check if we have the data as if we do the
  // webhook will not come back in and it will get stuck on the loading screen.
  /*const loadResumedCall = async () => {
    try {
      if (resumeId) {
        const callData = await makeRequest('calls/' + resumeId, 'GET')

        if (callData.data.summary && callData.data.summary !== '') {
          setNotes(callData.data.summary)
          setAiSummary(callData.data.summary ?? null)
          setAiHighlights(callData.data.highlights ?? null)
          setAiFailed(false)
          setAiFinished(true)
          setVoicemail(callData.data.is_voicemail ?? false)
          setViewing(callData.data.viewing ?? false)
          setInstruction(callData.data.instruction ?? false)
          setAppraisal(callData.data.appraisal ?? false)
          if (resumeRef.current) {
            clearTimeout(resumeRef.current)
            resumeRef.current = null
          }
        }
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Load Resume Call Failed',
          callId: resumeId,
          crmKey: crmKey,
        },
      })
    }
  }*/

  const setNoteType: (useSummary: boolean) => void = useCallback(async (useSummary: boolean) => {
    setSummaryUsed(useSummary);
    if (useSummary) {
      setNotes(aiSummary ?? 'No summary generated')
    } else {
      setNotes(aiHighlights ? aiHighlights.replace(/\\n/g, '\n') : 'No highlights generated')
    }
  }, [aiSummary, aiHighlights])

  useEffect(() => {
    if((resumeId || resume) && !gleapInitialized.current) {
      Gleap.initialize('TR7lgdpnWjMLKcAoNUFtSXFKBRvz7gLq');
      gleapInitialized.current = true;
    }

    if (resumeId) {
      const resumeItem = resume.find((item: any) => item.id === resumeId)

      if (!resumeItem) {
        return
      }

      // if we don't they may want to resume
      // if resume record exists
      setCallId(resumeId)
      setLoading(false)
      setDestination(resumeItem.destination)
      setAiFinished(resumeItem.summary ? true : false)
      setCallInProgress(false)
      setCrmKey(resumeItem.crm_key)
      setCrmUrl(resumeItem.crm_url.replace(/^\/+/, ''))
      setAiSummary(resumeItem.summary ?? 'No summary generated')
      setAiHighlights(resumeItem.highlights ?? '')
      setNotes(resumeItem.summary ?? 'No summary generated')
      setVoicemail(resumeItem.is_voicemail ?? false)
      setViewing(resumeItem.viewing ?? false)
      setInstruction(resumeItem.instruction ?? false)
      setAppraisal(resumeItem.appraisal ?? false)

      loadObjections()
      setCallDirection(resumeItem.call_direction ?? 'outbound')
      if (resumeItem.call_direction == 'inbound') {
        setIncoming(true)
        setVoicemail(false)
        setFollowUpRequired(false)
      } else {
        setIncoming(false)
      }

      // Set up the timeout only when the call is answered
      // if (!resumeItem.summary || resumeItem.summary == '') {
      //   const timer = setTimeout(() => {
      //     if (notes === '') {
      //       setAiFailed(true)
      //       setAiFinished(true)
      //       setNotes('No summary generated')
      //     }
      //   }, timeoutDuration)
      //
      //   resumeRef.current = timer
      //
      //   // check to see if we have the data
      //   loadResumedCall()
      // }
    }
  }, [resumeId])

  useEffect(() => {
    if (!Gleap.isUserIdentified() && userEmail && userName && userId) {
      Gleap.identify(userId, { email: userEmail.toString(), name: userName.toString() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId, resume, userEmail, userName, userId])

  useEffect(() => {
    if (cancelId) {
      const cancelItem = resume.find((item: any) => item.id === cancelId)

      if (!cancelItem) {
        return
      }

      const callRequest = {
        call_ended: true,
        call_state: 'Call Ended - App not saved',
        follow_up_synced: true,
        follow_up_type: 'none',
        note_synced: true,
      }

      try {
        makeRequest('calls/' + cancelId, 'PUT', callRequest)
      } catch (error) {
        setLoading(false)
        setSaving(false)
        Sentry.captureException(error, {
          extra: {
            message: `Failed to update call - Client Unaware - ${error}`,
            callId: cancelId,
            crmKey: cancelItem.crm_key,
          },
        })
      }

      setResume((prevResume: any) => prevResume.filter((item: any) => item.id !== cancelId))
    }
  }, [cancelId])

  useEffect(() => {
    if (callId !== '') {
      const pusher = new Pusher(pusherKey as string, {
        cluster: 'eu',
        forceTLS: true,
      })

      const channel = pusher.subscribe(callId)

      document.addEventListener('sessionEnded', () => {
        pusher.disconnect()
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current)
          timeoutIdRef.current = null
        }
      })

      channel.bind('note.synced', (data: any) => {
        if (data.note_synced === true) {
          setSaving(false)
          setFinished(true)
          pusher.unsubscribe(callId)
          pusher.disconnect()
        }
      })

      // Cleanup the pusher connection when the component unmounts or callId changes
      return () => {
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current)
          timeoutIdRef.current = null
        }
        pusher.unsubscribe(callId)
        pusher.disconnect()
      }
    }
  }, [callId, aiFinished, crmUrl, dateSetRef])

  const onSubmit: () => void = useCallback(async () => {
    setValidation([])
    setValidationItems([])
    let hasErrors = false

    if (appraisal && !actionDate) {
      setValidationItems((prevItems) => [...prevItems, 'appraisalDate'])
      setValidation((prevItems) => [...prevItems, 'You need to choose and appraisal date'])
      hasErrors = true
    }

    if (followUpRequired && !followUpDate) {
      setValidationItems((prevItems) => [...prevItems, 'followUpDate'])
      setValidation((prevItems) => [...prevItems, 'You need to choose and follow up date'])
      hasErrors = true
    }

    // if (!incoming && !voicemail && !appraisal && !viewing && !instruction && !applicants && crmKey && !objectionId && categoryId) {
    //   setValidationItems((prevItems) => [...prevItems, 'objectionId'])
    //   setValidation((prevItems) => [...prevItems, 'You need to choose an objection'])
    //   hasErrors = true
    // }

    if (hasErrors) {
      return
    }

    setSaving(true)

    const request = {
      status: 'Called',
      crm_key: crmKey,
      crm_url: crmUrl,
      call_id: callId,
    } as { [key: string]: any };

    const callRequest = {
      call_ended: true,
      call_state: 'Call Ended - CRM Plugin',
      notes: notes,
      highlights: aiHighlights,
      summary_used: summaryUsed,
      follow_up_type: followUpRequired ? 'followUp' : null,
      viewing: viewing,
      instruction: instruction,
      appraisal: appraisal,
      is_voicemail: voicemail,
      objection_id: objectionId,
      whatsapp: true,
      message_template_id: messageTemplateId,
      message_template_body: messageTemplateBody,
    } as { [key: string]: any };

    if (followUpRequired && followUpDate) {
      request['date_follow_up'] = followUpDate
      callRequest['chosen_date'] = followUpDate
    } else {
      request['date_follow_up'] = null
    }

    if (voicemailSummary) {
      callRequest['force_summarise'] = 1
    }

    if (appraisal && actionDate) {
      request['action_date'] = actionDate
      callRequest['chosen_date'] = actionDate
    }

    try {
      await makeRequest('calls/' + callId, 'PUT', callRequest)
    } catch (error) {
      setLoading(false)
      // setError(`Failed to update call - ${error}`)
      Sentry.captureException(error, {
        extra: {
          message: `Failed to update call - Client Unaware - ${error}`,
          callId: callId,
          crmKey: crmKey,
        },
      })
    }

    // Once the Notes go up to the CRM a pusher event is fired which
    // will close the modal on the plugin!


  }, [
    crmKey,
    crmUrl,
    callId,
    notes,
    viewing,
    instruction,
    appraisal,
    objectionId,
    voicemail,
    followUpType,
    actionDate,
    followUpDate,
    followUpRequired,
    voicemailSummary,
  ])

  const onCancelResume: () => void = useCallback(async () => {
    setResumeId('')
    setCallId(resumeId)
    setAiSummary('')
    setAiHighlights('')
    setDestination('')
    setAiFinished(false)
    setCallInProgress(false)
    setCrmKey('')
    setCrmUrl('')
    setNotes('')
    setVoicemail(false)
    setSummaryUsed(true)
  }, [])

  const onAbandon: () => void = useCallback(async () => {
    setFinished(true)

    const request = {
      status: 'Called',
      crm_key: crmKey,
      crm_url: crmUrl,
      call_id: callId,
    }

    const callRequest = {
      call_ended: true,
      call_state: 'Call Ended - CRM PLUGIN -  App not saved',
      notes: notes,
      highlights: aiHighlights,
      viewing: viewing,
      instruction: instruction,
      appraisal: appraisal,
      is_voicemail: voicemail,
      objection_id: objectionId,
      follow_up_synced: true,
      follow_up_type: 'none',
      note_synced: true,
    }

    try {
      await makeRequest('calls/' + callId, 'PUT', callRequest)
    } catch (error) {
      setLoading(false)
      setSaving(false)
      Sentry.captureException(error, {
        extra: {
          message: `Failed to update call - Client Unaware - ${error}`,
          callId: callId,
          crmKey: crmKey,
        },
      })
    }

    setSaving(false)
    setFinished(true)
  }, [
    crmKey,
    crmUrl,
    callId,
    notes,
    viewing,
    instruction,
    appraisal,
    objectionId,
    voicemail,
    followUpType,
    actionDate,
    followUpDate,
    followUpRequired,
  ])

  const authenticateUser = async () => {
    try {

      if (!loginToken) {
        setLoginFailed(true)
        return;
      }
      const authRequest = await fetch(apiPath + '/login-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: loginToken,
          resume: crmKey ? false : true,
        }),
      })

      const auth = await authRequest.json()
      setBearerToken(auth['bearer-token'])
      setXAgency(auth['x-prospector-agency'])
      setUserName(auth['x-prospector-user-name'])
      setUserEmail(auth['x-prospector-user-email'])
      setUserId(auth['x-prospector-user-id'])
      setStitchEnabled(auth['x-stitch-enabled'])
      setVoicemailSummary(auth['x-prospector-post-voicemail-summary'])
      setFollowUpType(auth['x-user-follow-up-type'] || 'both')
      setDisableFollowUp(auth['x-prospector-disable-follow-up'])
      setFollowUpRequired(!auth['x-prospector-disable-follow-up'])
      setOutageMessage(auth['x-prospector-outage-message'] || '')
      setOutageSeverity(auth['x-prospector-outage-severity'] || 0)

      if (!contactKey && auth['x-call-resume'] && auth['x-call-resume'] !== '') {
        try {
          let resumeData = JSON.parse(auth['x-call-resume']);
          setResume(resumeData)
          if (resumeData && resumeData[0].summary) {
            setAutoResume(resumeData)
          }
        } catch (e) {
          console.error('Invalid x-call-resume JSON', e);
        }
      }

      if (!contactKey && auth['x-call-awaiting-summary'] && auth['x-call-awaiting-summary'] !== '') {
        try {
          let result = JSON.parse(auth['x-call-awaiting-summary']);
          setCallsAwaitingSummary(result)
        } catch (e) {
          console.error('Invalid x-call-awaiting-summary JSON', e);
        }
      }

      if (auth['x-prospector-rps-app-settings']) {
        const settings = auth['x-prospector-rps-app-settings']
        try {
          let parsedSettings = settings
          if (typeof settings === 'string') {
            parsedSettings = JSON.parse(settings)
          }
          // Check if either display_number or routing_number is missing or empty
          if (!parsedSettings.display_number || !parsedSettings.routing_number) {
            setSettingsRequired(true)
            const authRequest = await fetch(apiPath + '/show-error', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                token: uuid + crmKey,
              }),
            })
          }
        } catch (error) {
          setSettingsRequired(true) // Optionally set settings required on parse error
        }
      } else {
        setSettingsRequired(true)
      }

      if (auth.message && auth.message.login) {
        setLoading(false)
        setLoginFailed(true)
        sendToContentScript(AUTHENTICATION_FAILED)
        return;
      }
    } catch (error) {
      sendToContentScript(AUTHENTICATION_FAILED)
      setLoading(false)
      setLoginFailed(true)
      Sentry.captureException(error, {
        extra: {
          callId: callId,
          crmKey: crmKey,
        },
      })
    }
  }

  const setAutoResume = function (resumeData: any) {
    if (resumeData && resumeData[0] && resumeData[0].updated_at) {
      const createdAt = new Date(resumeData[0].updated_at); // parsed as UTC because of the Z
      const now = new Date();
      const diffMs = now.getTime() - createdAt.getTime(); // milliseconds difference
      const diffMinutes = diffMs / (1000 * 60);
      if (diffMinutes <= 10) {
        setResumeId(resumeData[0].id ?? '');
      }
    }
  }


  const setFollowUpDateValue = function (date: string) {
    setFollowUpDate(date);
    //console.log('setFollowUpDateValue', date);
    dateSetRef.current = true;
  }


  const setActionDateValue = function (date: string) {
    setActionDate(date);
    //console.log('setActionDateValue', date);
    dateSetRef.current = true;
  }


  const loadObjections = async () => {
    try {
      const objectionResponse = await makeRequest('objections', 'GET')
      setObjections(objectionResponse.data)
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Objections Failed',
          callId: callId,
          crmKey: crmKey,
        },
      })
    }
  }

  const tryAgain = function () {
    setAiFinished(false)
    retryTimeout = setTimeout(() => {
      setAiFinished(true)
    }, timeoutDuration)
  }

  const startVoipCall = async (crmKey: string, endpoint: string, crm: string, destination: string, leadCategory: string) => {
    if (callInProgress || settingsRequired) return

    if (outageSeverity && outageSeverity == 1) {
      return
    }

    // on backend check if phone number belongs to a vendor and related to this property
    // if so flag so its saved in both locations.

    try {
      setCallInProgress(true)

      // Trigger actual call
      if (disableCalls) {
        setLoading(false)
        setCallInProgress(false)
        setShortCall(true)
      } else {

        //crmKey: string, destination: string,endpoint: string, crm: string
        const sessionResponse = await createSessionRequest(crmKey, endpoint, crm, destination, leadCategory, callTriggerUrl)

        setCallId(sessionResponse.data.id)
        //setCategoryId()
        setStitchDefaults([
          sessionResponse.data.vendor_name,
          sessionResponse.data.lead_address,
          sessionResponse.data.full_name,
        ])
        sendToContentScript(CALL_IN_PROGRESS)
      }
    } catch (error) {
      sendToContentScript(CALL_FAILED)
      setLoading(false)
      const authRequest = await fetch(apiPath + '/show-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: uuid + crmKey,
        }),
      })
      Sentry.captureException(error, {
        extra: {
          message: `Failed to startVoipCall - Customer Informed Lead Incorrect ${error}`,
          callId: callId,
          crmKey: crmKey,
        },
      })
    } finally {
      setCallInProgress(false)
    }
  }


  const onChangeMessageTemplate = (templateId: any) => {
    setMessageTemplateId(templateId)
    const template: string = messageTemplates.find((m) => m.id === templateId)?.message ?? ''

    if (template) {
      const placeholders = template.match(/\{\{\d+\}\}/g) || []

      setTemplatePlaceholders(placeholders)

      const initialPlaceholderValues: Record<string, string> = {}
      placeholders.forEach((placeholder, index) => {
        initialPlaceholderValues[placeholder] = stitchDefaults[index] ?? ''
      })

      setTemplatePlaceholderValues(initialPlaceholderValues)

      setSelectedMessageTemplate(template)
      const messageBody = getFilledTemplate(template, placeholders, initialPlaceholderValues)
      setMessageTemplateBody(messageBody)
    }
  }

  const handlePlaceholderChange = (value: any, placeholder: string) => {
    setTemplatePlaceholderValues({
      ...templatePlaceholderValues,
      [placeholder]: value,
    })
    const messageBody = getFilledTemplate()
    setMessageTemplateBody(messageBody)
  }

  const onBypassAI: () => void = useCallback(async () => {

    setOmitAi(true);
    setAiFinished(true);
    setVoicemail(true);
    setFollowUpRequired(false);
    setVoicemailSummary(false);

  }, [])

  const getFilledTemplate = (
    selectedTemplate: string = selectedMessageTemplate,
    placeholders: string[] = templatePlaceholders,
    placeholderValues: Record<string, string> = templatePlaceholderValues,
  ): string => {
    let filledTemplate = selectedTemplate
    if (filledTemplate.length === 0) return ''

    placeholders.forEach((placeholder) => {
      filledTemplate = filledTemplate.replace(placeholder, placeholderValues[placeholder] || placeholder)
    })
    return filledTemplate
  }

  const generatePageTitle = () => {
    let message = '';

    if (resume && !contactKey) {
      if (incoming) {
        message = `Save incoming call from: ${destination}`;
      } else {
        message = `Resume call details for: ${destination}`;
      }
    } else if (noAnswer) {
      message = 'No Answer';
    }
    else {
      message = 'Call finished';
    }

    return message;
  }
  const generatePageSubTitle = () => {
    let message = '';

    if (resume && !contactKey) {
      message = 'Please continue to save these details to your CRM before attempting another call';
    } else if (noAnswer) {
      message = 'You can choose to leave a note on the CRM';
    }
    else {
      message = 'Now the call has ended please confirm the details to go back into your CRM.';
    }

    return message;
  }

  const handleAutoCallEdit = () => {
    setToggleAutoCallHeader(!toggleAutoCallHeader);
  }

  const getAutoCallHeaders = () => {
    return {
      "Voicemail": voicemail ? 'Yes' : 'No',
      "Viewing Booked": viewing ? 'Yes' : 'No',
      "Appraisal Booked": appraisal ? 'Yes' : 'No',
      "Instruction Won": instruction ? 'Yes' : 'No'
    }
  }

  useEffect(() => {
    if (appraisal === true) {
      setFollowUpRequired(false)
    } else {
      setFollowUpRequired(!disableFollowUp)
    }
  }, [appraisal])

  useEffect(() => {
    if (destination) {
      setDisplayLoadingText("Call Connecting - Once finished We will notify you when a summary is available")
    }
  }, [destination])

  // show when full outage in the loading call area.
  if (outageSeverity && outageSeverity == 1 && destination)
    return (
      <InProgressBlock displayLoadingText={outageMessage} />
    )

    // show when full outage in the loading call area.
    if (outageSeverity && outageSeverity == 1 && !destination)
      return (
        <MaintenanceBlock outageMessage={outageMessage} />
      )
  

  if (finished)
    return (
      <FinishedBlock />
    )

  if (loginFailed)
    return (
      <LoginFailedBlock />
    )

  if (settingsRequired)
    return (
      <UpdateSettingsBlock />
    )

  if (resume && !resumeId)
    return (
      <ResumeBlock resumeData={resume} callInProgressData={callsAwaitingSummary} setResumeId={setResumeId} setCancelId={setCancelId} outageMessage={outageMessage} outageSeverity={outageSeverity} />
    )

  if (!crmKey && !destination && !resume && bearerToken)
    return (
      <InProgressBlock displayLoadingText={'Congratulations! There are no more calls left to resume!'} />
    )

  if (loading)
    return (
      <InProgressBlock displayLoadingText={displayLoadingText} />
    )

  if (saving)
    return (
      <SaveToCrm />
    )

  return (
    <div className='relative'>
      <PageHeader
        avatar={{
          src: noAnswer ? 'no-answer.png' : 'complete.png',
          type: 'image',
        }}
        hasMaxWidth
        pageTitle={{
          children: generatePageTitle(),
          hasBoldText: true,
        }}
        pageSubtitle={{
          children: generatePageSubTitle()
        }}
      />
      {outageMessage ? <OutageMemo outageMessage={outageMessage} outageSeverity={outageSeverity} /> : null}
      <FlexContainer hasMaxWidth isFlexColumn>
        <Tile>
          {validation.length > 0 ? (
            <Snack style={{ width: '100%', marginBottom: '20px' }} intent="danger" icon="warning">
              <div
                dangerouslySetInnerHTML={{
                  __html: validation.join('<br />'),
                }}
              />
            </Snack>
          ) : null}

          {(
            <>
              {resumeId && (
                <>
                  {!toggleAutoCallHeader ? <AutoCallHeader headers={getAutoCallHeaders()} onclick={handleAutoCallEdit} /> : null}
                  <Notification
                    storageKey="notificationAutoCallFlow"
                    message={
                      <>
                        <strong>New Feature</strong> - <strong>Viewings</strong> and <strong>Market Appraisals</strong>&nbsp;
                        are now detected automatically!
                      </>
                    }
                  />
                  {toggleAutoCallHeader && (
                    <Grid className="">
                      {!incoming ? (
                        <ColQuarter className="el-mb6 el-pl5 el-pr5">
                          <p className="el-mb3">Was Voicemail</p>
                          <Toggle
                            key={voicemail ? 'yes' : 'no'}
                            style={{ width: '100%' }}
                            hasGreyBg
                            id="voicemail"
                            defaultChecked={voicemail}
                            onChange={(e) => setVoicemail(e.currentTarget.checked)}
                          >
                            <ElToggleItem className="toggle-yes">Yes</ElToggleItem>
                            <ElToggleItem className="toggle-no">No</ElToggleItem>
                          </Toggle>
                        </ColQuarter>
                      ) : null}

                      {!voicemail && !hideViewing && (
                        <ColQuarter className="el-mb6 el-pl5 el-pr5">
                          <p className="el-mb3">Viewing Booked</p>
                          <Toggle
                            key={viewing ? 'yes' : 'no'}
                            style={{ width: '100%' }}
                            hasGreyBg
                            id="viewings"
                            defaultChecked={viewing}
                            onChange={(e) => setViewing(e.currentTarget.checked)}
                          >
                            <ElToggleItem className="toggle-yes">Yes</ElToggleItem>
                            <ElToggleItem className="toggle-no">No</ElToggleItem>
                          </Toggle>
                        </ColQuarter>
                      )}

                      {!voicemail && !hideAppraisal && (
                        <ColQuarter className="el-mb6 el-pl5 el-pr5">
                          <p className="el-mb3">Appraisal Booked</p>

                          <Toggle
                            key={appraisal ? 'yes' : 'no'}
                            style={{ width: '100%' }}
                            hasGreyBg
                            id="appraisal"
                            defaultChecked={appraisal}
                            onChange={(e) => setAppraisal(e.currentTarget.checked)}
                          >
                            <ElToggleItem className="toggle-yes">Yes</ElToggleItem>
                            <ElToggleItem className="toggle-no">No</ElToggleItem>
                          </Toggle>
                        </ColQuarter>
                      )}

                      {!voicemail && !hideAppraisal && appraisal && (
                        <ColQuarter className="el-mb6 el-pl5 el-pr5">
                          <p className={`el-mb3 ${validationItems.includes('appraisalDate') ? 'error' : ''}`}>
                            Date of Appraisal
                          </p>
                          <Input
                            className="el-w12"
                            type="date"
                            value={actionDate}
                            onChange={(e) => setActionDateValue(e.target.value)}
                          />
                        </ColQuarter>
                      )}

                      {!voicemail && !appraisal && (
                        <ColQuarter className="el-mb6 el-pl5 el-pr5">
                          <p className="el-mb3">Instruction Won</p>
                          <Toggle
                            key={instruction ? 'yes' : 'no'}
                            style={{ width: '100%' }}
                            hasGreyBg
                            id="instruction"
                            defaultChecked={instruction}
                            onChange={(e) => setInstruction(e.currentTarget.checked)}
                          >
                            <ElToggleItem className="toggle-yes">Yes</ElToggleItem>
                            <ElToggleItem className="toggle-no">No</ElToggleItem>
                          </Toggle>
                        </ColQuarter>
                      )}

                      {(!appraisal && !disableFollowUp) && (
                        <ColQuarter className="el-mb6 el-pl5 el-pr5">
                          <p className="el-mb3">Follow Up Required</p>
                          <Toggle
                            key={followUpRequired ? 'yes' : 'no'}
                            style={{ width: '100%' }}
                            hasGreyBg
                            id="followUpRequired"
                            defaultChecked={followUpRequired}
                            onChange={(e) => setFollowUpRequired(e.currentTarget.checked)}
                          >
                            <ElToggleItem className="toggle-yes">Yes</ElToggleItem>
                            <ElToggleItem className="toggle-no">No</ElToggleItem>
                          </Toggle>
                        </ColQuarter>
                      )}

                      {followUpRequired && (
                        <ColQuarter className="el-mb6 el-pl5 el-pr5">
                          <p className={`el-mb3 ${validationItems.includes('followUpDate') ? 'error' : ''}`}>
                            Date of Follow Up
                          </p>
                          <Input
                            className="el-w12"
                            type="date"
                            value={followUpDate}
                            onChange={(e) => setFollowUpDateValue(e.target.value)}
                          />
                        </ColQuarter>
                      )}
                    </Grid>
                  )}
                </>
              )}

              {disableFollowUp && (
                <div className="el-pl5 el-pr5 el-mt10">
                  <PersistentNotification intent="low" className="el-mb6" isExpanded isFullWidth isInline>
                    <div className="el-mb5">
                      <TextBase hasBoldText>Follow Up Disabled</TextBase>
                    </div>
                    <p>
                      Please note that follow-up functionality is currently disabled for your agency. As a result, a follow-up will not be scheduled for this call.
                      If you would like this feature to be enabled, please contact your administrator.
                    </p>

                  </PersistentNotification>
                </div>
              )}

              <div className={`el-pl5 el-pr5 el-pt5 el-mt5`}>
                <div className="el-flex el-flex-row el-flex-justify-between">
                  <span className="el-mb3">
                    <TextBase hasBoldText>Call Notes</TextBase>
                  </span>
                  <div className="el-mb3 el-flex el-flex-row">
                    {voicemail ? (
                      <Toggle
                        key={voicemailSummary ? 'yes' : 'no'}
                        style={{ width: '100%' }}
                        hasGreyBg
                        id="voicemailSummary"
                        defaultChecked={voicemailSummary}
                        onChange={(e) => setVoicemailSummary(e.currentTarget.checked)}
                      >
                        <ElToggleItem className="toggle-yes">On</ElToggleItem>
                        <ElToggleItem className="toggle-no">Off</ElToggleItem>
                      </Toggle>
                    ) : null}
                    {aiHighlights && (voicemailSummary || !voicemail) ? (
                      <div className='el-pl5'>
                        <Toggle
                          style={{ width: '100%' }}
                          hasGreyBg
                          id="noteType"
                          defaultChecked={true}
                          onChange={(e) => setNoteType(e.currentTarget.checked)}
                        >
                          <ElToggleItem>Summary</ElToggleItem>
                          <ElToggleItem>Highlights</ElToggleItem>
                        </Toggle></div>
                    ) : null}
                  </div>
                </div>
                {!voicemail || (voicemail && voicemailSummary) ? (
                    <>
                      <TextArea
                          value={
                            summaryUsed
                                ? aiSummary
                                : aiHighlights
                                    ? aiHighlights.replace(/\\n/g, '\n')
                                    : 'No highlights generated'
                          }
                          className="el-mb7"
                          onChange={(e) => {
                            if (summaryUsed) {
                              setAiSummary(e.target.value)
                              setNotes(e.target.value)
                            } else {
                              setAiHighlights(e.target.value)
                              setNotes(e.target.value)
                            }
                          }}
                          style={{ width: '100%', height: '300px' }}
                      />

                      {aiSummary && aiHighlights && (
                        <small
                        style={{
                        fontSize: '14px',
                        color: '#2563eb', // visible blue
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                  > ℹ️ Call <strong>{summaryUsed ? 'summary' : 'highlights'}</strong> will be treated as the call note.</small>
                )}
                  </>
                ) : null}



                {voicemail && !voicemailSummary ? (
                  <div className="el-mb5">
                    <TextBase hasDisabledText>Voicemail notes currently switched off.</TextBase>
                  </div>
                ) : null}
              </div>

              {!resume.incoming_id && !voicemail && !appraisal && !viewing && !instruction && !applicants && crmKey && categoryId && !noAnswer && (
                <div className="el-pl5 el-pr5 el-border-grey-t el-pt7">
                  <p className={`el-mb3 el-mt-5 ${validationItems.includes('objectionId') ? 'error' : ''}`}>
                    Objection Met
                  </p>
                  <Select className="el-mb6 el-w-full" onChange={(e) => setObjectionId(e.target.value)} required>
                    <option data-testid="option" value="">
                      Select an option
                    </option>
                    {objections
                      .filter((objection) => categoryId ? objection.category_id === categoryId : true)
                      .map((objection) => (
                        <option key={objection.id} data-testid="option" value={objection.id}>
                          {objection.name}
                        </option>
                      ))}
                  </Select>
                </div>
              )}

              {stitchEnabled && destination && destination.startsWith('07') ? (
                <label
                  className="el-pl5 el-pr5 el-mb8 el-flex el-flex-row el-border-grey-t el-pt5"
                  style={{ alignItems: 'center' }}
                >
                  <Input
                    className="el-w12"
                    type="checkbox"
                    name="whatsapp"
                    onChange={(e) => setWhatsappFollowup(e.target.checked)}
                  />
                  <p className="el-pl5">
                    <TextBase hasBoldText>Send Whatsapp Follow up</TextBase>
                  </p>
                </label>
              ) : null}

              {whatsappFollowup ? (
                <CardWrap className="el-card-focussed">
                  <Grid>
                    <ColHalf>
                      <div className="el-pl5 el-pr5">
                        <p className="el-mb3">
                          <TextBase hasBoldText>Message Template</TextBase>
                        </p>
                        <Select
                          className="el-mb6 el-w-full"
                          onChange={(e) => onChangeMessageTemplate(e.target.value)}
                          required
                        >
                          <option data-testid="option" value="">
                            Select an option
                          </option>
                          {Array.isArray(messageTemplates) && messageTemplates.length > 0 ? (
                            messageTemplates
                              .filter((m) => m.name?.startsWith('phone_') || m.name?.startsWith('prospector_'))
                              .map((m) => (
                                <option key={m.id} value={m.id}>
                                  {m.name}
                                </option>
                              ))
                          ) : (
                            <option disabled>No templates available</option>
                          )}
                        </Select>
                      </div>

                      {templatePlaceholders.length > 0 && (
                        <>
                          {templatePlaceholders.map((placeholder, index) => (
                            <div key={index} className="el-pl5 el-pr5 el-pb5">
                              <TextBase hasBoldText>
                                Enter text for Template Part {index} - {placeholder}:
                              </TextBase>
                              <Input
                                className="el-w12"
                                type="text"
                                value={templatePlaceholderValues[placeholder] as string}
                                onChange={(e) => handlePlaceholderChange(e.target.value, placeholder)}
                                onBlur={(e) => handlePlaceholderChange(e.target.value, placeholder)}
                              />
                            </div>
                          ))}
                        </>
                      )}
                    </ColHalf>
                    <ColHalf>
                      {messageTemplateId ? (
                        <div className="el-pl5 el-pr5">
                          <p className="el-mb3">
                            <TextBase hasBoldText>Message Body</TextBase>
                          </p>
                          <TextArea
                            value={getFilledTemplate()}
                            style={{ width: '100%', height: '200px' }}
                            disabled={true}
                          >
                            {getFilledTemplate()}
                          </TextArea>
                        </div>
                      ) : null}
                    </ColHalf>
                  </Grid>
                </CardWrap>
              ) : null}

              <div className="el-pl5 el-pr5 el-mt10">
                <Button className="el-mr5" intent="primary" type="button" onClick={onSubmit}>
                  Save Details
                </Button>
                <Button className="el-mr5" intent="danger" type="button" onClick={onAbandon}>
                  Do not save to CRM
                </Button>
              </div>
            </>
          )}
        </Tile>
      </FlexContainer>
      {resumeId && (
        <>
          <Button intent="danger" type="button" className='cancel-autoresume' onClick={onCancelResume}>
            Cancel Resume
          </Button>
        </>
      )}
    </div>
  )
}

// Function to send data to content script
const sendToContentScript = (data: any) => {
  console.log('post message')
  window.parent.postMessage(
    { action: "PP_NOTIFICATION", data },
    "*"
  );
};

export default Wrapper
